import { apiClient } from '@/lib/api-client';

export interface EmployeeSummary {
  id: string;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  date: string;
}

export interface DashboardStats {
  totalEmployees: number;
  activeEmployees?: number;
  inactiveEmployees?: number;
  presentToday: number;
  onLeave: number;
  pendingApprovals: number;
  hasReportees?: boolean;
  recentHires: number;
  monthlyPayroll?: number;
  avgSalary?: number;
  upcomingBirthdays: EmployeeSummary[];
  upcomingAnniversaries: EmployeeSummary[];
}

export interface ActivityItem {
  id: string;
  type:
    | 'EMPLOYEE_JOINED'
    | 'EMPLOYEE_LEFT'
    | 'LEAVE_REQUESTED'
    | 'LEAVE_APPROVED'
    | 'LEAVE_REJECTED'
    | 'ATTENDANCE_MARKED'
    | 'PAYROLL_PROCESSED'
    | 'DOCUMENT_UPLOADED';
  description: string;
  timestamp: string;
  actor?: string;
  actorId?: string;
  subjectId?: string;
}

export interface RecentActivity {
  activities: ActivityItem[];
  total: number;
}

export interface QuickAction {
  id: string;
  label: string;
  description: string;
  href: string;
  icon: string;
  permission: string;
}

export interface OnboardingStep {
  key: string;
  label: string;
  completed: boolean;
  href: string;
}

export interface OnboardingProgress {
  steps: OnboardingStep[];
  completedCount: number;
  totalCount: number;
  dismissed: boolean;
}

class DashboardService {
  private basePath = '/hrms/dashboard';

  /**
   * Get dashboard statistics
   */
  async getStats(): Promise<DashboardStats> {
    return apiClient.get<DashboardStats>(`${this.basePath}/stats`);
  }

  /**
   * Get recent activity
   */
  async getRecentActivity(limit = 10): Promise<RecentActivity> {
    return apiClient.get<RecentActivity>(`${this.basePath}/activity`, {
      params: { limit },
    });
  }

  /**
   * Get available quick actions
   */
  async getQuickActions(): Promise<QuickAction[]> {
    return apiClient.get<QuickAction[]>(`${this.basePath}/quick-actions`);
  }

  /**
   * Get onboarding checklist progress
   */
  async getOnboardingProgress(): Promise<OnboardingProgress> {
    return apiClient.get<OnboardingProgress>(`${this.basePath}/onboarding`);
  }

  /**
   * Dismiss onboarding checklist
   */
  async dismissOnboarding(): Promise<void> {
    return apiClient.post(`${this.basePath}/onboarding/dismiss`);
  }
}

export const dashboardService = new DashboardService();
